import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "card custom-shadow rounded-3 bg-white border mb-4" }
const _hoisted_2 = { class: "card-body p-4" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "col-12" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = { class: "default-table-area style-two campaigns-table" }
const _hoisted_8 = { class: "table-responsive" }
const _hoisted_9 = { class: "table align-middle border-0" }
const _hoisted_10 = { class: "ps-0" }
const _hoisted_11 = ["innerHTML"]
const _hoisted_12 = { class: "fs-12" }
const _hoisted_13 = ["innerHTML"]
const _hoisted_14 = ["innerHTML"]
const _hoisted_15 = { class: "text-end" }
const _hoisted_16 = { class: "text-end fw-medium" }
const _hoisted_17 = { key: 0 }
const _hoisted_18 = { class: "d-flex justify-content-between align-items-center mt-3" }
const _hoisted_19 = { class: "d-flex align-items-center" }
const _hoisted_20 = {
  key: 0,
  "aria-label": "Page navigation"
}
const _hoisted_21 = { class: "pagination justify-content-center mb-0" }
const _hoisted_22 = ["onClick"]

import { computed, ref } from 'vue';
import { t } from '@/utils/i18nHelper';


export default /*@__PURE__*/_defineComponent({
  __name: 'LiquidityScheduleTable',
  props: {
  schedule: {
    type: Object,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const sortedEvents = computed(() => {
  if (!props.schedule || !props.schedule.events) return [];
  // Sort events by date
  return [...props.schedule.events].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
});

// --- Pagination Logic ---
const currentPage = ref(1);
const itemsPerPage = ref(5); // Show 5 items per page

const totalPages = computed(() => {
  return Math.ceil(sortedEvents.value.length / itemsPerPage.value);
});

const paginatedEvents = computed(() => {
  if (sortedEvents.value.length === 0) return [];
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return sortedEvents.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const goToPage = (page: number) => {
  currentPage.value = page;
};

const onItemsPerPageChange = () => {
  currentPage.value = 1; // Reset to first page when changing items per page
};
// --- End Pagination Logic ---

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  return new Date(dateString).toLocaleDateString('it-IT', options);
};

// Uso diretto della funzione t() nel template

const formatCurrency = (value: number) => {
  if (typeof value !== 'number') return 'N/A';
  return value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

const getAmountClass = (isIncome: boolean) => {
  return isIncome ? 'border-success' : 'border-danger';
};

const getRowBorderClass = (isIncome: boolean) => {
  return isIncome ? 'border-success' : 'border-danger';
};

const hexToRgba = (hex: string, alpha: number) => {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        return `rgba(108, 117, 125, ${alpha})`;
    }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// --- Styling for Category & Type Labels ---

const categoryColors: { [key: string]: string } = {
  IRES: '#dc3545',        // Rosso - coerente con TaxBreakdownChart
  INPS: '#fd7e14',        // Arancione - coerente con TaxBreakdownChart
  INPS_FISSI: '#ffc107',  // Giallo - coerente con TaxBreakdownChart
  // Casse Professionali
  SOGGETTIVO: '#fd7e14',  // Arancione (come INPS Variable)
  INTEGRATIVO: '#ffc107', // Giallo (come INPS Fixed)
  MATERNITA: '#9c27b0',   // Viola (specifico per maternità)
  // Altri
  ACCUMULO: '#6a7cf9',
  DEFAULT: '#6c757d'
};

const getCategoryLabel = (category: string) => {
  const keyMap = {
    'IRES': 'fr.deadlines.category_taxes',
    'INPS': 'fr.deadlines.category_inps_variable',
    'INPS_FISSI': 'fr.deadlines.category_inps_fixed',
    // Casse Professionali
    'SOGGETTIVO': 'fr.deadlines.category_contrib_soggettivo',
    'INTEGRATIVO': 'fr.deadlines.category_contrib_integrativo',
    'MATERNITA': 'fr.deadlines.category_contrib_maternita',
    // Altri
    'ACCUMULO': 'fr.liquidity.avg_monthly_rate'
  };

  const defaultLabels = {
    'IRES': 'Imposte',
    'INPS': 'INPS Variabili',
    'INPS_FISSI': 'INPS Fissi',
    // Casse Professionali
    'SOGGETTIVO': 'Contrib. Soggettivo',
    'INTEGRATIVO': 'Contrib. Integrativo',
    'MATERNITA': 'Contrib. Maternità',
    // Altri
    'ACCUMULO': 'Rata'
  };

  const key = keyMap[category as keyof typeof keyMap];
  const defaultLabel = defaultLabels[category as keyof typeof defaultLabels] || category;

  return key ? t(key, defaultLabel) : defaultLabel;
};

const getCategoryLabelStyle = (category: string) => {
  const color = categoryColors[category] || categoryColors.DEFAULT;
  return {
    backgroundColor: hexToRgba(color, 0.1),
    color: color,
    fontSize: '12px',
  };
};

const getTypeStyle = (status: string) => {
  let color = categoryColors.DEFAULT;
  if (status === 'Versamento') {
    color = categoryColors.ACCUMULO; // Use Rata/Accumulo color
  } else if (status && status.startsWith('Pagamento')) {
    color = '#fd5812'; // Use Danger color from theme
  }
  return {
    backgroundColor: hexToRgba(color, 0.1),
    color: color,
    fontSize: '12px',
  };
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("h4", {
            class: "mb-0 fw-semibold",
            innerHTML: _unref(t)('fr.liquidity.planning_title', 'Pianificazione delle rate')
          }, null, 8, _hoisted_5),
          _createElementVNode("p", {
            class: "mb-3",
            innerHTML: _unref(t)('fr.liquidity.planning_description', 'Ogni mese accantoni la cifra giusta. Così quando arriva la scadenza sei già pronto, senza sorprese.')
          }, null, 8, _hoisted_6)
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("table", _hoisted_9, [
            _cache[2] || (_cache[2] = _createElementVNode("thead", null, [
              _createElementVNode("tr", null, [
                _createElementVNode("th", {
                  scope: "col",
                  class: "ps-0"
                }, "Evento"),
                _createElementVNode("th", { scope: "col" }, "Categoria"),
                _createElementVNode("th", { scope: "col" }, "Tipo"),
                _createElementVNode("th", {
                  scope: "col",
                  class: "text-end"
                }, "Importo"),
                _createElementVNode("th", {
                  scope: "col",
                  class: "text-end"
                }, "Saldo")
              ])
            ], -1)),
            _createElementVNode("tbody", null, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedEvents.value, (event, index) => {
                return (_openBlock(), _createElementBlock("tr", { key: index }, [
                  _createElementVNode("td", _hoisted_10, [
                    _createElementVNode("div", {
                      class: _normalizeClass(['border-start', 'border-2', 'ps-3', 'py-0', getRowBorderClass(event.isIncome)])
                    }, [
                      _createElementVNode("h6", {
                        class: "fs-14 fw-semibold mb-0",
                        innerHTML: event.description
                      }, null, 8, _hoisted_11),
                      _createElementVNode("span", _hoisted_12, _toDisplayString(formatDate(event.date)), 1)
                    ], 2)
                  ]),
                  _createElementVNode("td", null, [
                    _createElementVNode("span", {
                      class: "d-inline-block p-2 rounded-2 fw-semibold",
                      style: _normalizeStyle(getCategoryLabelStyle(event.category)),
                      innerHTML: getCategoryLabel(event.category)
                    }, null, 12, _hoisted_13)
                  ]),
                  _createElementVNode("td", null, [
                    _createElementVNode("span", {
                      class: "d-inline-block p-2 rounded-2 fw-semibold",
                      style: _normalizeStyle(getTypeStyle(event.status)),
                      innerHTML: event.status
                    }, null, 12, _hoisted_14)
                  ]),
                  _createElementVNode("td", _hoisted_15, [
                    _createElementVNode("span", {
                      class: _normalizeClass(getAmountClass(event.isIncome))
                    }, _toDisplayString(formatCurrency(event.amount)), 3)
                  ]),
                  _createElementVNode("td", _hoisted_16, _toDisplayString(formatCurrency(event.balanceAfter)), 1)
                ]))
              }), 128)),
              (paginatedEvents.value.length === 0)
                ? (_openBlock(), _createElementBlock("tr", _hoisted_17, [...(_cache[1] || (_cache[1] = [
                    _createElementVNode("td", {
                      colspan: "5",
                      class: "text-center py-4"
                    }, "Nessun evento da visualizzare.", -1)
                  ]))]))
                : _createCommentVNode("", true)
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_18, [
          _createElementVNode("div", _hoisted_19, [
            _cache[4] || (_cache[4] = _createElementVNode("label", { class: "me-2 fw-medium fs-14" }, "Righe per pagina:", -1)),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((itemsPerPage).value = $event)),
              onChange: onItemsPerPageChange,
              class: "form-select form-select-sm",
              style: {"width":"auto"}
            }, [...(_cache[3] || (_cache[3] = [
              _createElementVNode("option", { value: 5 }, "5", -1),
              _createElementVNode("option", { value: 10 }, "10", -1),
              _createElementVNode("option", { value: 20 }, "20", -1),
              _createElementVNode("option", { value: 30 }, "30", -1)
            ]))], 544), [
              [_vModelSelect, itemsPerPage.value]
            ])
          ]),
          (totalPages.value > 1)
            ? (_openBlock(), _createElementBlock("nav", _hoisted_20, [
                _createElementVNode("ul", _hoisted_21, [
                  _createElementVNode("li", {
                    class: _normalizeClass(["page-item", { 'disabled': currentPage.value === 1 }])
                  }, [
                    _createElementVNode("a", {
                      class: "page-link",
                      href: "#",
                      onClick: _withModifiers(prevPage, ["prevent"])
                    }, [...(_cache[5] || (_cache[5] = [
                      _createElementVNode("i", { class: "ri-arrow-left-line" }, null, -1)
                    ]))])
                  ], 2),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(totalPages.value, (page) => {
                    return (_openBlock(), _createElementBlock("li", {
                      class: _normalizeClass(["page-item", { 'active': page === currentPage.value }]),
                      key: page
                    }, [
                      _createElementVNode("a", {
                        class: "page-link",
                        href: "#",
                        onClick: _withModifiers(($event: any) => (goToPage(page)), ["prevent"])
                      }, _toDisplayString(page), 9, _hoisted_22)
                    ], 2))
                  }), 128)),
                  _createElementVNode("li", {
                    class: _normalizeClass(["page-item", { 'disabled': currentPage.value === totalPages.value }])
                  }, [
                    _createElementVNode("a", {
                      class: "page-link",
                      href: "#",
                      onClick: _withModifiers(nextPage, ["prevent"])
                    }, [...(_cache[6] || (_cache[6] = [
                      _createElementVNode("i", { class: "ri-arrow-right-line" }, null, -1)
                    ]))])
                  ], 2)
                ])
              ]))
            : _createCommentVNode("", true)
        ])
      ])
    ])
  ]))
}
}

})